import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  getQuestionBanksByFolder,
  getQuestionBanksInstitutionByFolder,
  getQuestionBankResult,
  getQuestionBankInstitutionResult,
} from "../helper/apicalls/questionBank";
import StartTestCard from "../components/StartTestCard";
import TestResults from "../components/TestResults";
import "../styles/MaterialFolder.scss";

const QuestionBankFolderTestPage = () => {
  const { folderid, testid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [selectedBankForTest, setSelectedBankForTest] = useState(null);
  const [showStartTestCard, setShowStartTestCard] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const searchParams = new URLSearchParams(location.search);
  const isInstitution = searchParams.get("type") === "institution";

  useEffect(() => {
    const fetchBank = async () => {
      if (!folderid || !testid) return;
      try {
        setLoading(true);
        const res = isInstitution
          ? await getQuestionBanksInstitutionByFolder({ folderId: folderid })
          : await getQuestionBanksByFolder({ folderId: folderid });

        const data = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : [];

        const selectedBank = data.find(
          (bank) => (bank.id || bank._id) === testid,
        );
        if (selectedBank) {
          setSelectedBankForTest(selectedBank);
          if (selectedBank.completed) {
            try {
              const resultResponse = isInstitution
                ? await getQuestionBankInstitutionResult({
                    questionbank_folders: folderid,
                    question_bank: testid,
                  })
                : await getQuestionBankResult({
                    questionbank_folders: folderid,
                    question_bank: testid,
                  });

              const r = resultResponse?.data || resultResponse || {};

              const normalizedResult = {
                score: r.score ?? r.total_score ?? 0,
                totalQuestions:
                  r.totalQuestions ??
                  r.total_questions ??
                  (Array.isArray(r.questions) ? r.questions.length : 0),
                answers: r.answers || r.user_answers || {},
                questions: r.questions || [],
                pass_percentage: r.pass_percentage,
              };

              setTestResult(normalizedResult);
              setShowStartTestCard(false);
            } catch (e) {
              console.error("Failed to load question bank result", e);
              setTestResult(null);
              setShowStartTestCard(true);
            }
          } else {
            setTestResult(null);
            setShowStartTestCard(true);
          }
        } else {
          setSelectedBankForTest(null);
          setShowStartTestCard(false);
          setTestResult(null);
        }
      } catch (e) {
        console.error("Failed to load question bank for test page", e);
        setSelectedBankForTest(null);
        setShowStartTestCard(false);
        setTestResult(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBank();
  }, [folderid, testid, isInstitution]);

  const handleStartTest = () => {
    if (!selectedBankForTest) return;
    const testId = selectedBankForTest.id || selectedBankForTest._id;
    if (!testId) return;

    navigate(`/test/${testId}?practice=true&parent_id=${folderid}${isInstitution ? "&type=institution" : ""}`, {
      state: {
        questionBank: selectedBankForTest,
      },
    });
  };

  const handleRetest = () => {
    if (testid) {
      Cookies.remove(`test_result_${testid}`);
    }
    setTestResult(null);
    setShowStartTestCard(true);
    handleStartTest();
  };

  const handleShowTestAnswers = () => {
    if (!testid) return;
    navigate(`/test/${testid}?practice=true&review=true&parent_id=${folderid}${isInstitution ? "&type=institution" : ""}`);
  };

  if (!folderid || !testid) {
    return (
      <div className="materials-page">
        <div className="material-folder-empty">Invalid test link.</div>
      </div>
    );
  }

  return (
    <div className="materials-page">
      {loading && (
        <div className="materials-loading">Loading test details...</div>
      )}

      {!loading && !selectedBankForTest && (
        <div className="material-folder-empty">Test not found.</div>
      )}

      {!loading && selectedBankForTest && (
        <>
          {testResult ? (
            <TestResults
              score={testResult.score}
              totalQuestions={testResult.totalQuestions}
              userData={testResult.answers}
              questions={testResult.questions}
              pass_percentage={testResult.pass_percentage}
              onRetest={handleRetest}
              onShowAnswer={handleShowTestAnswers}
              page_type="test"
            />
          ) : (
            showStartTestCard && (
              <StartTestCard
                title={
                  selectedBankForTest?.name ||
                  selectedBankForTest?.title ||
                  "Practice Test"
                }
                durationMinutes={selectedBankForTest?.duration || 30}
                totalQuestions={selectedBankForTest?.content?.length || 0}
                onStart={handleStartTest}
              />
            )
          )}
        </>
      )}
    </div>
  );
};

export default QuestionBankFolderTestPage;
